import { useFormik } from 'formik';
import * as yup from 'yup';
import { AlertCircle, FileText, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SideDrawer } from '@/components/drawer/SideDrawer';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { SummaryStrip } from '@/components/drawer/SummaryStrip';
import { StatusBadge } from '@/components/StatusBadge';
import { FieldError } from '@/components/form/FieldError';
import { MonthPicker } from '@/components/MonthPicker';
import { getApiErrorMessage } from '@/api/errorHandler';
import { PolicyAPIRequestMapper, PolicyUpdateRequestMapper } from '@/api/mapper/policy';
import { createPolicy, updatePolicy } from '@/api/service/policy';
import { useRefreshPolicyData } from '@/api/query/refresh';
import type { NomineeRelationship } from '@/api/type/policy';
import type { Member, Policy } from '@/types';
import { formatCurrency, MONTHLY_FEE, PLAN_MONTHS } from '@/lib/payments';
import { currentMonthKey, formatMonthKey } from '@/lib/months';
import { getPolicyEndMonth, NOMINEE_RELATIONSHIP_OPTIONS, POLICY_PLAN_AMOUNT } from '@/lib/policies';
import { FIELD_CLASS, LABEL_CLASS, PRIMARY_BUTTON_CLASS, SELECT_FIELD_CLASS } from '@/lib/styles';
import { toLettersOnly } from '@/lib/utils';

interface UpsertPolicyProps {
  member: Member | null;
  /** The member's current policies: listed below the form and used for the next policy number. */
  policies: Policy[];
  /** Set to edit this policy; leave out to add a new one. */
  policy?: Policy | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PolicyFormValues {
  cardNo: string;
  /** 'YYYY-MM', sent as start_month. */
  startMonth: string;
  nomineeName: string;
  nomineeRelationship: string;
}

const FORM_ID = 'add-policy-form';

const CARD_NO_MAX_DIGITS = 12;

/** Policy number the server will assign: "P" + member_id + 2-digit sequence. */
const nextPolicyNo = (memberId: string, policyCount: number) =>
  `P${memberId}${String(policyCount + 1).padStart(2, '0')}`;

/**
 * Add Policy drawer: creates another 100-month policy for a member.
 * With `policy` set it becomes Edit Policy, to correct mistakes. The start month can only
 * change while no payments are recorded, since due months and interest are worked out from it.
 */
export function UpsertPolicy({ member, policies: existingPolicies, policy, open, onOpenChange }: UpsertPolicyProps) {
  const refreshPolicyData = useRefreshPolicyData();
  const isEdit = Boolean(policy);
  const canChangeStart = !policy || policy.paidMonths === 0;

  const formik = useFormik<PolicyFormValues>({
    enableReinitialize: true,
    initialValues: policy
      ? {
          cardNo: policy.cardNo ?? '',
          startMonth: policy.startMonth,
          nomineeName: policy.nomineeName ?? '',
          nomineeRelationship: policy.nomineeRelationship ?? '',
        }
      : { cardNo: '', startMonth: currentMonthKey(), nomineeName: '', nomineeRelationship: '' },
    validationSchema: yup.object({
      cardNo: yup.string().matches(/^\d*$/, 'Card Number must be digits only').max(CARD_NO_MAX_DIGITS),
      startMonth: yup.string().required('Please select start month'),
      nomineeName: yup.string().trim().required('Please enter nominee name'),
      nomineeRelationship: yup.string().required('Please select nominee relationship'),
    }),
    onSubmit: async (values, { setStatus, resetForm }) => {
      if (!member) return;
      setStatus(undefined);
      try {
        if (policy) {
          await updatePolicy(
            Number(policy.id),
            PolicyUpdateRequestMapper({
              cardNo: values.cardNo,
              startMonth: canChangeStart ? values.startMonth : undefined,
              nomineeName: values.nomineeName,
              nomineeRelationship: values.nomineeRelationship as NomineeRelationship,
            }),
          );
        } else {
          await createPolicy(
            PolicyAPIRequestMapper({
              memberId: member.id,
              startMonth: values.startMonth,
              cardNo: values.cardNo,
              nomineeName: values.nomineeName,
              nomineeRelationship: values.nomineeRelationship as NomineeRelationship,
            }),
          );
        }
        await refreshPolicyData();
        resetForm();
        onOpenChange(false);
      } catch (error) {
        setStatus(getApiErrorMessage(error));
      }
    },
  });

  const showError = (name: keyof PolicyFormValues) =>
    Boolean((formik.touched[name] || formik.submitCount > 0) && formik.errors[name]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && formik.isSubmitting) return;
    if (!nextOpen) formik.resetForm();
    onOpenChange(nextOpen);
  };

  return (
    <SideDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title={isEdit ? 'Edit Policy' : 'Add Policy'}
      member={member}
      footer={
        <>
          {formik.status && (
            <div className="mr-auto flex min-w-0 flex-1 items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{formik.status}</span>
            </div>
          )}
          <Button type="button" variant="outline" className="border-gray-200" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={formik.isSubmitting}
            className={`min-w-36 ${PRIMARY_BUTTON_CLASS}`}
          >
            {formik.isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Policy'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={formik.handleSubmit} className="space-y-5">
        <DrawerSection
          icon={FileText}
          title={isEdit ? 'Policy Details' : 'New Policy Details'}
          description="Card number, start month and nominee for this policy"
          aside={
            member && (
              <span
                title={isEdit ? 'Policy number' : 'Policy number (assigned on save)'}
                className="rounded-md bg-gray-100 px-2 py-0.5 text-[13px] font-semibold tabular-nums text-gray-800"
              >
                {policy?.policyNo ?? nextPolicyNo(member.memberNo ?? member.id, existingPolicies.length)}
              </span>
            )
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`${FORM_ID}-card`} className={`${LABEL_CLASS} flex justify-between`}>
                Card Number
                <span className="text-xs text-gray-400">Optional</span>
              </Label>
              <Input
                id={`${FORM_ID}-card`}
                name="cardNo"
                inputMode="numeric"
                placeholder="Enter card number"
                value={formik.values.cardNo}
                onChange={(e) =>
                  formik.setFieldValue('cardNo', e.target.value.replace(/\D/g, '').slice(0, CARD_NO_MAX_DIGITS))
                }
                onBlur={formik.handleBlur}
                className={FIELD_CLASS}
              />
              <FieldError show={showError('cardNo')} message={formik.errors.cardNo} />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${FORM_ID}-start`} className={LABEL_CLASS}>
                Start Month *
              </Label>
              <MonthPicker
                id={`${FORM_ID}-start`}
                value={formik.values.startMonth}
                onSelect={(monthKey) => formik.setFieldValue('startMonth', monthKey)}
                disabled={!canChangeStart}
              />
              {canChangeStart ? (
                <FieldError show={showError('startMonth')} message={formik.errors.startMonth} />
              ) : (
                <p className="text-xs text-gray-500">
                  Can't be changed: payments are already recorded for this policy.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${FORM_ID}-nominee`} className={LABEL_CLASS}>
                Nominee Name *
              </Label>
              <Input
                id={`${FORM_ID}-nominee`}
                name="nomineeName"
                placeholder="Enter nominee name"
                value={formik.values.nomineeName}
                onChange={(e) => formik.setFieldValue('nomineeName', toLettersOnly(e.target.value))}
                onBlur={formik.handleBlur}
                className={FIELD_CLASS}
              />
              <FieldError show={showError('nomineeName')} message={formik.errors.nomineeName} />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${FORM_ID}-relationship`} className={LABEL_CLASS}>
                Nominee Relationship *
              </Label>
              <Select
                value={formik.values.nomineeRelationship || undefined}
                onValueChange={(value: string) => {
                  formik.setFieldValue('nomineeRelationship', value);
                  formik.setFieldTouched('nomineeRelationship', true, false);
                }}
              >
                <SelectTrigger id={`${FORM_ID}-relationship`} className={SELECT_FIELD_CLASS}>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {NOMINEE_RELATIONSHIP_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError show={showError('nomineeRelationship')} message={formik.errors.nomineeRelationship} />
            </div>
          </div>

          <SummaryStrip
            items={[
              { label: 'Duration', value: `${PLAN_MONTHS} months` },
              { label: 'Monthly Amount', value: formatCurrency(MONTHLY_FEE) },
              {
                label: 'End Month',
                value: formik.values.startMonth ? formatMonthKey(getPolicyEndMonth(formik.values.startMonth)) : '--',
              },
              { label: 'Total Plan Value', value: formatCurrency(POLICY_PLAN_AMOUNT) },
            ]}
          />
        </DrawerSection>

        {!isEdit && (
          <DrawerSection
            icon={ListChecks}
            title="Existing Policies"
            count={existingPolicies.length}
            description="This member's current policies"
            padded={false}
          >
            {existingPolicies.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {existingPolicies.map((policy) => (
                  <li key={policy.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{policy.policyNo}</p>
                      <p className="text-xs text-gray-500">Started {formatMonthKey(policy.startMonth)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[13px] tabular-nums text-gray-500">
                        {policy.cardNo && `Card ${policy.cardNo} · `}
                        {policy.paidMonths} / {PLAN_MONTHS} months
                      </span>
                      <StatusBadge status={policy.status} />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-5 py-4 text-sm text-gray-500">No policies yet. This will be the first policy.</p>
            )}
          </DrawerSection>
        )}
      </form>
    </SideDrawer>
  );
}
